import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { nativeFileUpload } from 'sprites-types/workflow/tools/nativeFileUpload'
import { FileResult } from '../shared/FileResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeFileUploadDisplayComponent({
  nodeExecution,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeFileUpload, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Convert result to array of files for FileResult display
  const rawFiles = Array.isArray(result) ? result : [result]

  // Convert to FileResult format
  const files = rawFiles.map((file) => ({
    name: file.filename,
    mimeType: file.fileType,
    url: file.source.kind === 'url' ? file.source.value : undefined,
    downloadUrl: file.source.kind === 'url' ? file.source.value : undefined,
  }))

  return (
    <div className="space-y-2">
      {files.map((file, index) => (
        <FileResult key={index} file={file} onOpenPanel={onOpenPanel} showPreview={true} />
      ))}
    </div>
  )
}

// Download function for file upload results
async function downloadFileUploadResults(nodeExecution: NodeExecution): Promise<void> {
  try {
    const schema = getToolResultSchema(nativeFileUpload, nodeExecution.inputs)
    const parseResult = schema.safeParse(nodeExecution.outputs)

    if (!parseResult.success) {
      console.error('Schema validation failed for download:', parseResult.error)
      return Promise.resolve()
    }

    const result = parseResult.data
    const rawFiles = Array.isArray(result) ? result : [result]

    // Download all files that have URLs
    let downloadCount = 0
    rawFiles.forEach((file) => {
      if (file.source.kind === 'url') {
        const link = document.createElement('a')
        link.href = file.source.value
        link.download = file.filename
        link.click()
        downloadCount++
      }
    })

    if (downloadCount > 0) {
      toast.success(`Downloaded ${downloadCount} file${downloadCount > 1 ? 's' : ''}`)
    } else {
      toast.error('No downloadable files found')
    }
  } catch (error) {
    console.error('Error downloading file upload results:', error)
    toast.error('Failed to download files')
  }
}

// Convert file upload results to markdown
function nativeFileUploadToMarkdown(nodeExecution: NodeExecution): string {
  try {
    const schema = getToolResultSchema(nativeFileUpload, nodeExecution.inputs)
    const parseResult = schema.safeParse(nodeExecution.outputs)

    if (!parseResult.success) {
      return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
    }

    const result = parseResult.data
    const rawFiles = Array.isArray(result) ? result : [result]

    const sections: string[] = ['## File Upload\n']
    sections.push(`Uploaded ${rawFiles.length} file${rawFiles.length > 1 ? 's' : ''}:\n`)

    rawFiles.forEach((file, index) => {
      sections.push(`### ${index + 1}. ${file.filename}`)
      sections.push(`- **Type**: ${file.fileType}`)
      if (file.source.kind === 'url') {
        sections.push(`- **Download**: [${file.filename}](${file.source.value})`)
      } else {
        sections.push(`- **Content**: Raw data`)
      }
      sections.push('')
    })

    sections.push('\n---\n')
    return sections.join('\n')
  } catch (error) {
    return `## File Upload\n\nError parsing results: ${error}\n\n---\n`
  }
}

// Export the component with download and markdown support
export const NativeFileUploadDisplay = createWorkflowToolComponent(
  NativeFileUploadDisplayComponent,
  downloadFileUploadResults,
  nativeFileUploadToMarkdown
)
